import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spin, Pagination } from 'antd';
import { UserRating } from '../../../services/api';
import { PageRoute, RatingSortOption } from '../../../models/enums';
import { selectLoadedUser } from '../../../routes/Auth/auth.ducks';
import RatingsFilters from '../../../components/RatingsFilters';
import RatingCard from '../../../components/RatingCard';
import HelmetComponent from '../../../utils/HelmetHelper';
import styles from './myRatings.scss';
import history from '../../../history';
import config from 'appConfig';
import { calculateTotalPagingCount, notification, scrollToTop } from '../../../services';
import { MyRatingsSkeleton } from '../MyRatingsSkeleton';

interface MyRatingsProps {
  location?: {
    search?: string;
  };
}

const MyRatings: React.FC<MyRatingsProps> = ({ location }) => {
  const user = useSelector(selectLoadedUser);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(null);
  const [sortOption, setSortOption] = useState(RatingSortOption.Newest);
  const [ratingCounts, setRatingCounts] = useState({ totalCount: 0, averageRating: null });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const userId = user ? user.userId : undefined;

  // Read filter from URL query parameters on mount
  useEffect(() => {
    if (location && location.search) {
      const params = new URLSearchParams(location.search);

      // Read type filter
      const typeParam = params.get('type');
      if (typeParam) {
        const typeValue = parseInt(typeParam, 10);
        if (typeValue === 1 || typeValue === 2) {
          setFilterType(typeValue);
        }
      }

      // Read sort option
      const sortParam = params.get('sortOption');
      if (sortParam) {
        const sortValue = parseInt(sortParam, 10);
        if (sortValue >= 1 && sortValue <= 4) {
          setSortOption(sortValue);
        }
      }
    }
  }, [location]);

  const fetchRatings = async () => {
    try {
      setLoading(true);

      const response = await UserRating.list({
        userId,
        type: filterType,
        page: page,
        itemPerPage: config.maxAdPerPage,
        sortOption: sortOption
      });

      setRatings(response.data || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update URL query string when filters change
  useEffect(() => {
    if (!location) return;

    const params = new URLSearchParams();

    if (filterType !== null) {
      params.set('type', filterType.toString());
    }

    if (sortOption !== RatingSortOption.Newest) {
      params.set('sortOption', sortOption.toString());
    }

    const newSearch = params.toString();
    const currentSearch = location.search ? location.search.substring(1) : '';

    // Only update if the query string has changed
    if (newSearch !== currentSearch) {
      const newPath = `${PageRoute.myRatings}${newSearch ? `?${newSearch}` : ''}`;
      history.replace(newPath);
    }
  }, [filterType, sortOption]);

  useEffect(() => {
    if (userId) {
      fetchRatings();
    }
  }, [userId, filterType, sortOption, page]);

  // Fetch rating counts
  useEffect(() => {
    const fetchRatingCounts = async () => {
      try {
        const response = await UserRating.getOverallCount(userId);
        setRatingCounts(response);
      } catch (error) {
        console.error('Error fetching rating counts:', error);
        setRatingCounts({ totalCount: 0, averageRating: null });
      }
    };

    if (userId) {
      fetchRatingCounts();
    }
  }, [userId]);

  const handleAnswerSuccess = () => {
    notification.show('Odgovor je uspješno poslan');
    fetchRatings();
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    scrollToTop();
  };

  const handleFilterTypeChange = (value: number | null) => {
    setFilterType(value);
    setPage(1);
  };

  if (!user) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <React.Fragment>
      <HelmetComponent
        title={`${"Ocjene"} | ${"INDEX OGLASI"}`}
        ogTitle={`${"Ocjene"} | ${"INDEX OGLASI"}`}
      />
      <div>
        <div className={styles.mainContent}>
          <RatingsFilters
            filterType={filterType}
            onFilterTypeChange={handleFilterTypeChange}
            sortOption={sortOption}
            onSortChange={setSortOption}
            averageRating={ratingCounts.averageRating}
            totalCount={ratingCounts.totalCount}
            loading={loading}
          />

          {loading ? (
            <MyRatingsSkeleton />
          ) : ratings.length === 0 ? (
            <div className={styles.emptyState}>
              Još nemate ocjena.
            </div>
          ) : (
            <React.Fragment>
              <div className={styles.ratingsContainer}>
                {ratings.map(rating => (
                  <RatingCard
                    key={rating.id}
                    rating={rating}
                    userProfile={user}
                    onAnswerSuccess={handleAnswerSuccess}
                  />
                ))}
              </div>
              {totalCount > config.maxAdPerPage || page > 1 ? (
                <Pagination
                  total={calculateTotalPagingCount(totalCount, page)}
                  current={page}
                  pageSize={config.maxAdPerPage}
                  onChange={handlePageChange}
                  className={styles.pagination}
                />
              ) : null}
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default MyRatings;



// WEBPACK FOOTER //
// ./src/containers/MyRatingsPage/MyRatings/index.tsx